<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">Daily Check-in</h2>
        <button class="close-button" @click="closeModal">✕</button>
      </div>
      
      <!-- Content -->
      <div class="modal-body">
        <p class="modal-description">
          Отмечайте вход каждый день без пропусков, чтобы получать награды
        </p>
        
        <!-- Warning message -->
        <div class="warning-message">
          <span class="warning-icon">⚠️</span>
          Пропуск любого дня сбрасывает прогресс и цикл начинается заново
        </div>
        
        <!-- Calendar Grid - Show all 31 days -->
        <div class="calendar-grid">
          <div 
            v-for="day in 31" 
            :key="day"
            class="day-cell"
            :class="getDayClass(day)"
            @click="selectDay(day)"
          >
            <div class="day-content">
              <div class="reward-amount">{{ formatReward(getRewardForDay(day)) }}</div>
            </div>
            <div class="day-number">{{ day }}</div>
            <div v-if="claimedDays.has(day)" class="checkmark">✔</div>
          </div>
        </div>
        
        <!-- Action Button -->
        <div class="modal-actions">
          <button 
            v-if="!claimedToday"
            class="claim-button" 
            @click="claimToday"
            :disabled="!canClaimToday"
          >
            Отметиться сегодня
          </button>
          <button 
            v-else
            class="claimed-button" 
            disabled
          >
            Отмечано
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';

export default {
  name: 'DailyCheckinModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'claimed'],
  setup(props, { emit }) {
    const currentDaySequence = ref(1); // Track current day sequence starting from 1
    const claimedDays = ref(new Set());
    const claimedToday = ref(false);
    
    // Load current day sequence from localStorage
    const loadCurrentDaySequence = () => {
      const saved = localStorage.getItem('daily_checkin_current_day');
      if (saved) {
        try {
          currentDaySequence.value = parseInt(saved) || 1;
        } catch (e) {
          console.error('Failed to load current day sequence:', e);
          currentDaySequence.value = 1;
        }
      }
    };
    
    // Save current day sequence to localStorage
    const saveCurrentDaySequence = () => {
      localStorage.setItem('daily_checkin_current_day', currentDaySequence.value.toString());
    };
    
    // Check if the last claim date was yesterday or today
    const updateDaySequence = () => {
      const lastClaimDate = localStorage.getItem('lastDailyClaimDate');
      const today = new Date();
      const todayStr = today.toDateString();
      
      if (lastClaimDate) {
        const lastClaim = new Date(lastClaimDate);
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        // If user claimed yesterday, increment the day sequence
        if (lastClaim.toDateString() === yesterday.toDateString()) {
          currentDaySequence.value++;
          saveCurrentDaySequence();
        } else if (lastClaim.toDateString() === todayStr) {
          // If user already claimed today, do nothing
          claimedToday.value = true;
        } else {
          // If user missed a day, reset to day 1
          currentDaySequence.value = 1;
          saveCurrentDaySequence();
        }
      } else {
        // First time user, start at day 1
        currentDaySequence.value = 1;
        saveCurrentDaySequence();
      }
    };
    
    // Load claimed days from localStorage
    const loadClaimedDays = () => {
      const saved = localStorage.getItem('daily_checkin_claimed_days');
      if (saved) {
        try {
          const days = JSON.parse(saved);
          claimedDays.value = new Set(days);
          claimedToday.value = claimedDays.value.has(currentDaySequence.value);
        } catch (e) {
          console.error('Failed to load claimed days:', e);
        }
      }
    };
    
    // Save claimed days to localStorage
    const saveClaimedDays = () => {
      localStorage.setItem('daily_checkin_claimed_days', JSON.stringify([...claimedDays.value]));
    };
    
    // Get reward amount for specific day (doubled rewards)
    const getRewardForDay = (day) => {
      if (day === 1) return 1000; // doubled
      if (day === 2) return 1400; // doubled
      if (day === 3) return 1800; // doubled
      if (day === 4) return 2200; // doubled
      if (day === 5) return 2600; // doubled
      if (day === 6) return 3000; // doubled
      if (day === 7) return 4000; // doubled
      if (day >= 8 && day <= 14) {
        return (2200 + (day - 8) * 175) * 2; // doubled 2200 → 7000
      }
      if (day === 15) return 10000; // doubled
      if (day >= 16 && day <= 20) {
        return (5500 + (day - 16) * 625) * 2; // doubled 5500 → 16000
      }
      if (day === 21) return 20000; // doubled
      if (day >= 22 && day <= 29) {
        return (11000 + (day - 22) * 1000) * 2; // doubled 11000 → 36000
      }
      if (day === 30) return 50000; // doubled
      if (day === 31) return 100000; // doubled
      return 0;
    };
    
    // Format reward number with spaces
    const formatReward = (amount) => {
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    
    // Get CSS class for day cell
    const getDayClass = (day) => {
      if (day === currentDaySequence.value && !claimedDays.value.has(day)) {
        return 'current-day';          // Current day in sequence, not claimed
      }
      if (claimedDays.value.has(day)) {
        return 'claimed-day';          // Already claimed
      }
      if (day > currentDaySequence.value) {
        return 'future-day';           // Future days
      }
      return 'past-day';               // Past unclaimed days
    };
    
    // Check if today can be claimed
    const canClaimToday = computed(() => {
      return currentDaySequence.value > 0 && currentDaySequence.value <= 31 && !claimedDays.value.has(currentDaySequence.value);
    });
    
    // Select a day (for visual feedback)
    const selectDay = (day) => {
      if (day === currentDaySequence.value && canClaimToday.value) {
        claimToday();
      }
    };
    
    // Claim today's reward
    const claimToday = () => {
      if (!canClaimToday.value) return;
      
      claimedDays.value.add(currentDaySequence.value);
      claimedToday.value = true;
      saveClaimedDays();
      
      const reward = getRewardForDay(currentDaySequence.value);
      emit('claimed', reward);
      
      // Update last claim date
      const today = new Date();
      localStorage.setItem('lastDailyClaimDate', today.toDateString());
      
      // Move to next day
      currentDaySequence.value++;
      saveCurrentDaySequence();
      
      if (window.showNotification) {
        window.showNotification(
          'Награда получена!',
          `Ежедневный вход: ${reward} монет`,
          'success'
        );
      }
    };
    
    // Close modal
    const closeModal = () => {
      emit('close');
    };
    
    // Load data on mount
    onMounted(() => {
      loadCurrentDaySequence();
      loadClaimedDays();
      updateDaySequence();
    });
    
    return {
      currentDaySequence,
      claimedDays,
      claimedToday,
      canClaimToday,
      getRewardForDay,
      formatReward,
      getDayClass,
      selectDay,
      claimToday,
      closeModal
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: #000000;
  background: linear-gradient(to bottom, #000000 0%, #333333 100%);
  border-radius: 18px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid #808080;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.8);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #2A2A2A;
}

.modal-title {
  color: #ffffff;
  font-size: 1.5rem;
  margin: 0;
  font-family: 'Inter', 'SF Pro', 'Manrope', sans-serif;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  color: #cccccc;
  font-size: 1.5rem;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
}

.modal-body {
  padding: 20px;
}

.modal-description {
  color: #cccccc;
  text-align: center;
  margin-bottom: 20px;
  font-size: 0.95rem;
  font-family: 'Inter', 'SF Pro', 'Manrope', sans-serif;
  line-height: 1.4;
}

.warning-message {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #808080;
  border-radius: 12px;
  padding: 12px 15px;
  margin-bottom: 25px;
  color: #aaaaaa;
  font-size: 0.85rem;
  font-family: 'Inter', 'SF Pro', 'Manrope', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
}

.warning-icon {
  font-size: 1rem;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 25px;
}

.day-cell {
  aspect-ratio: 1;
  background: #404040;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #606060;
  position: relative;
  overflow: hidden;
  font-family: 'Inter', 'SF Pro', 'Manrope', sans-serif;
}

.day-cell:hover:not(.future-day):not(.claimed-day) {
  transform: translateY(-2px);
  background: #333333;
}

.day-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.reward-amount {
  font-size: 0.7rem;
  color: #cccccc;
  font-weight: 500;
  text-align: center;
}

.day-number {
  position: absolute;
  bottom: 4px;
  font-size: 0.8rem;
  color: #FFFFFF;
  font-weight: 600;
}

.checkmark {
  position: absolute;
  top: 4px;
  right: 4px;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: bold;
}

/* Day States */
.current-day {
  background: #808080;
  border-color: #a9a9a9;
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.3);
}

.current-day:hover {
  background: #a9a9a9;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
}

.claimed-day {
  background: #555555;
  border-color: #777777;
}

.past-day {
  background: #444444;
  border-color: #666666;
  opacity: 0.7;
}

.future-day {
  background: #333333;
  border-color: #555555;
  cursor: not-allowed;
  opacity: 0.5;
}

.modal-actions {
  display: flex;
  justify-content: center;
}

.claim-button {
  padding: 14px 35px;
  background: #0a5c0a; /* Dark green */
  border: none;
  border-radius: 30px;
  color: #FFFFFF;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Inter', 'SF Pro', 'Manrope', sans-serif;
  box-shadow: 0 4px 15px rgba(128, 128, 128, 0.3);
  min-width: 200px;
}

.claim-button:hover:not(:disabled) {
  background: #a9a9a9;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(128, 128, 128, 0.4);
}

.claim-button:disabled {
  background: #666666;
  color: #bbbbbb;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.claimed-button {
  padding: 14px 35px;
  background: #2ecc71; /* Bright green */
  border: none;
  border-radius: 30px;
  color: #FFFFFF;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: not-allowed;
  transition: all 0.3s ease;
  font-family: 'Inter', 'SF Pro', 'Manrope', sans-serif;
  box-shadow: 0 4px 15px rgba(46, 204, 113, 0.3);
  min-width: 200px;
}

.warning-message {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #808080;
  border-radius: 12px;
  padding: 12px 15px;
  margin-bottom: 25px;
  color: #f39c12; /* Bright orange-yellow */
  font-size: 0.85rem;
  font-family: 'Inter', 'SF Pro', 'Manrope', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Responsive */
@media (max-width: 480px) {
  .modal-content {
    width: 95%;
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 15px;
  }
  
  .modal-body {
    padding: 15px;
  }
  
  .modal-title {
    font-size: 1.3rem;
  }
  
  .calendar-grid {
    gap: 6px;
  }
  
  .day-cell {
    border-radius: 10px;
  }
  
  .claim-button {
    padding: 12px 25px;
    font-size: 1rem;
    min-width: 180px;
  }
}
</style>